import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Receipt,
  TrendingUp,
  AlertCircle,
  Plus,
  RefreshCw,
} from "lucide-react";
import ExpenseStatusBadge from "./ExpenseStatusBadge";
import { Currency } from "@/components/i18n/Currency";
import expenseService from "@/services/expenseService";
import {
  ExpenseSummary,
  ExpenseStatus,
  EXPENSE_STATUS_INFO,
} from "@/types/expense";

interface ExpenseSummaryDashboardProps {
  onCreateExpense?: () => void;
  onRefresh?: () => void;
  onViewDrafts?: () => void;
  onViewRejected?: () => void;
  className?: string;
}

const ExpenseSummaryDashboard: React.FC<ExpenseSummaryDashboardProps> = ({
  onCreateExpense,
  onRefresh,
  onViewDrafts,
  onViewRejected,
  className,
}) => {
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchSummary = async () => {
    try {
      setIsLoading(true);
      const summaryData = await expenseService.getExpenseSummary();
      setSummary(summaryData);
    } catch (error: any) {
      console.error("Error fetching expense summary:", error);
      toast({
        title: "Error",
        description: "Failed to load expense summary",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleRefresh = () => {
    fetchSummary();
    onRefresh?.();
  };

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl">Expense Summary</h2>
          <Button variant="outline" disabled>
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            Loading...
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded animate-pulse mb-2" />
                <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl">Expense Summary</h2>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">
                Unable to load expense summary
              </p>
              <Button
                variant="outline"
                onClick={handleRefresh}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status: ExpenseStatus) => {
    const statusInfo = EXPENSE_STATUS_INFO[status];
    return statusInfo.color;
  };

  const getStatusIcon = (status: ExpenseStatus) => {
    switch (status) {
      case ExpenseStatus.DRAFT:
        return <FileText className="h-4 w-4" />;
      case ExpenseStatus.PENDING_APPROVAL:
        return <Clock className="h-4 w-4" />;
      case ExpenseStatus.APPROVED:
        return <CheckCircle className="h-4 w-4" />;
      case ExpenseStatus.REJECTED:
        return <XCircle className="h-4 w-4" />;
      case ExpenseStatus.INVOICED:
        return <Receipt className="h-4 w-4" />;
      case ExpenseStatus.PAID:
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Pending Expenses Alert */}
      {summary.pendingCount > 0 && (
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  You have {summary.pendingCount} pending expenses
                </p>
                <p className="text-sm text-muted-foreground">
                  Complete and submit them for approval
                </p>
              </div>
              <Button variant="outline" onClick={onViewDrafts}>
                <FileText className="h-4 w-4 mr-2" />
                View Pending
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl">Expense Summary</h2>
          <p className="text-sm text-muted-foreground">
            Overview of your contractor expenses
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Stats Cards - Mobile Optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Total Approved Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Approved</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              <Currency amount={parseFloat(summary.totalApprovedAmount)} />
            </div>
            <p className="text-xs text-muted-foreground">
              Total Approved Expense Value
            </p>
          </CardContent>
        </Card>

        {/* Pending (Draft) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Pending</CardTitle>
            <FileText className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{summary.pendingCount}</div>
            <p className="text-xs text-muted-foreground">
              <Currency amount={parseFloat(summary.pendingAmount)} /> draft
              expenses
            </p>
          </CardContent>
        </Card>

        {/* Approved (Invoiced) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{summary.approvedCount}</div>
            <p className="text-xs text-muted-foreground">
              <Currency amount={parseFloat(summary.approvedAmount)} /> invoiced
            </p>
          </CardContent>
        </Card>

        {/* Paid */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Paid</CardTitle>
            <Receipt className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{summary.paidCount}</div>
            <p className="text-xs text-muted-foreground">
              <Currency amount={parseFloat(summary.paidAmount)} /> paid
            </p>
          </CardContent>
        </Card>

        {/* Rejected */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{summary.rejectedCount}</div>
            <p className="text-xs text-muted-foreground">
              <Currency amount={parseFloat(summary.rejectedAmount)} /> rejected
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {summary.rejectedCount > 0 && (
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  You have {summary.rejectedCount} rejected expenses
                </p>
                <p className="text-sm text-muted-foreground">
                  Review feedback and resubmit if needed
                </p>
              </div>
              <Button variant="outline" onClick={onViewRejected}>
                <AlertCircle className="h-4 w-4 mr-2" />
                View Rejected
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExpenseSummaryDashboard;
